import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Grid,
  Link,
  Paper,
  ShipsIcon,
  StorageIcon,
  Tab,
  Tabs,
  TrendingUpIcon,
  Typography,
  CountryFlag,
  Skeleton,
  Stack
} from '@esgian/esgianui';
import { VesselsTable, VesselPortCallsTable } from '@components/Tables';
import { useParams, useSearchParams } from 'react-router-dom';
import KpiBox from '@components/Display/KpiBox';
import { formatNumberReadable, parseNumberToSignificant } from '@helpers';
import { GEOGRAPHICAL_TYPES } from '@constants';
import CompanyPortStatisticsSection from '@components/Sections/ShipAnalytics/CompanyPortStatisticsSection';
import FutureScheduleSection from '@components/Sections/ShipAnalytics/FutureScheduleSection';
import { TextWithTooltipIcon } from '@components';
import { useSegment } from '@hooks';
import { useFutureScheduleAvailability } from '@hooks/useFutureScheduleAvailability';

function OwnerProfileSection({
  setLastPortCalls,
  ownerData,
  loading,
  lastPortCalls,
  renderedOverviewMap
}) {
  const [tabSection, setTabSection] = useState(1);
  const [isFutureScheduleAvailable, setIsFutureScheduleAvailable] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const { segment } = useSegment();
  const params = useParams();

  const handleSectionChange = (_, val) => {
    if (!isFutureScheduleAvailable && val === 4) {
      return;
    }
    setTabSection(val);
    searchParams.set('section', val);
    setSearchParams(searchParams);
  };

  useEffect(() => {
    setTabSection(parseInt(searchParams.get('section') || 1));
  }, []);

  const ensureHttpsAndWww = (website) => {
    let site = website;
    if (!website.startsWith('www.') && !website.startsWith('https://')) {
      site = `https://www.${site}`;
    } else if (website.startsWith('www.')) {
      site = `https://${site}`;
    }
    return site;
  };

  const {
    country,
    address,
    website,
    averageAge,
    averageCapacity,
    fleetSize,
    totalCapacity,
    ownerLongName,
    ownerShortName,
    ownerVessels,
    isoAlpha2
  } = ownerData || {};
  let ownerId = ownerVessels?.[0]?.operatorId;

  let imoList = [];
  if (ownerData) {
    imoList = ownerData.ownerVessels.map((vessel) => vessel.imo);
  }

  const futurePortCallsQuery = useFutureScheduleAvailability({
    imoList,
    segmentId: segment.id,
    entityId: params.id,
    setIsAvailable: setIsFutureScheduleAvailable
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          spacing={1} // Applies spacing between items on the same row
          useFlexGap
          sx={{ flexWrap: 'wrap' }}>
          <KpiBox
            loading={loading}
            icon={<ShipsIcon />}
            value={fleetSize ? fleetSize : ''}
            title={'Fleet Size'}
            subtitle={'Vessels'}
          />
          <KpiBox
            loading={loading}
            icon={<TrendingUpIcon />}
            value={averageAge ? parseNumberToSignificant(averageAge) : ''}
            title={'Average Age'}
            subtitle={'Years'}
          />
          <KpiBox
            loading={loading}
            icon={<StorageIcon />}
            value={averageCapacity ? formatNumberReadable(parseInt(averageCapacity)) : '-'}
            title={'Average Capacity'}
            subtitle={'CEU'}
          />
          <KpiBox
            loading={loading}
            icon={<StorageIcon />}
            value={totalCapacity ? formatNumberReadable(totalCapacity) : ''}
            title={'Total Capacity'}
            subtitle={'CEU'}
          />
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <Tabs value={tabSection} onChange={handleSectionChange} variant="scrollable">
            <Tab value={1} label="Overview" />
            <Tab value={2} label="Activity" />
            <Tab value={3} label="Port Statistics" />
            <Tab
              value={4}
              label={
                isFutureScheduleAvailable ? (
                  'FUTURE SCHEDULE'
                ) : (
                  <TextWithTooltipIcon
                    color={'text.secondary'}
                    label={'FUTURE SCHEDULE'}
                    tooltipText={'Upcoming port calls coming soon'}
                  />
                )
              }
              sx={{ cursor: isFutureScheduleAvailable ? 'pointer' : 'default' }}
              disableRipple={!isFutureScheduleAvailable}
            />
          </Tabs>
        </Paper>
      </Grid>
      {tabSection === 1 && (
        <>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, height: '100%' }}>
                  <Grid container spacing={2}>
                    <Grid item container spacing={2} xs={12}>
                      <Grid item alignSelf={'center'}>
                        <Typography variant={'h6'}>Overview</Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    {!loading.overview ? (
                      <Grid item container spacing={2}>
                        <Grid item xs={2.5}>
                          <Typography variant={'body2'} bold>
                            Name:{' '}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant={'body2'}>{`${ownerLongName}`}</Typography>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid item xs={8}>
                        <Skeleton />
                      </Grid>
                    )}
                    {!loading.overview ? (
                      <Grid item container spacing={2}>
                        <Grid item xs={2.5}>
                          <Typography variant={'body2'} bold>
                            Short Name:{' '}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant={'body2'}>{`${ownerShortName}`}</Typography>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid item xs={8}>
                        <Skeleton />
                      </Grid>
                    )}
                    {!loading.overview ? (
                      <Grid item container spacing={2}>
                        <Grid item xs={2.5}>
                          <Typography variant={'body2'} bold>
                            Country:{' '}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Stack direction={'row'} spacing={1} alignItems={'center'}>
                            <CountryFlag size={15} countryCode={isoAlpha2} />
                            <Typography variant={'body2'}>{country}</Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid item xs={8}>
                        <Skeleton />
                      </Grid>
                    )}
                    {!loading.overview ? (
                      <Grid item container spacing={2}>
                        <Grid item xs={2.5}>
                          <Typography variant={'body2'} bold>
                            Address:{' '}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant={'body2'}>{address}</Typography>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid item xs={8}>
                        <Skeleton />
                      </Grid>
                    )}
                    {!loading.overview ? (
                      <Grid item container spacing={2}>
                        <Grid item xs={2.5}>
                          <Typography variant={'body2'} bold>
                            Website:{' '}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          {website ? (
                            <Link
                              variant={'body2'}
                              target={'__blank'}
                              href={ensureHttpsAndWww(website)}>
                              {website}
                            </Link>
                          ) : (
                            <Typography variant={'body2'}>{website}</Typography>
                          )}
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid item xs={8}>
                        <Skeleton />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={8}>
                <Grid container>
                  <Grid item xs={12}>
                    {renderedOverviewMap}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper>
              <VesselsTable
                hideColumns={[
                  'grossTonnage',
                  'deadweight',
                  'ownerLongName',
                  'maxLiftingCapacity',
                  'numCranes'
                ]}
                title={'Owner Vessels'}
                loading={loading}
                vessels={ownerVessels}
                fileName={`fleet-list-${ownerShortName}`}
              />
            </Paper>
          </Grid>
        </>
      )}
      {tabSection === 2 && (
        <Grid item xs={12}>
          <VesselPortCallsTable
            geographicalType={GEOGRAPHICAL_TYPES.ALL}
            ownerId={ownerId}
            setPortCallData={setLastPortCalls}
            name={ownerShortName}
            hideColumns={['operatorName', 'countryName']}
            portCalls={lastPortCalls}
            loading={loading}
            title={'Last Port Calls'}
          />
        </Grid>
      )}
      {tabSection === 3 && (
        <CompanyPortStatisticsSection
          companyType="owner"
          companyId={ownerId}
          companyName={ownerLongName}
        />
      )}
      {tabSection === 4 && <FutureScheduleSection futurePortCallsQuery={futurePortCallsQuery} />}
    </Grid>
  );
}

OwnerProfileSection.propTypes = {
  ownerData: PropTypes.object,
  vessels: PropTypes.arrayOf(PropTypes.object),
  lastPortCalls: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  setLastPortCalls: PropTypes.func.isRequired,
  renderedOverviewMap: PropTypes.node
};

OwnerProfileSection.defaultProps = {
  ownerData: null,
  lastPortCalls: null,
  loading: false,
  vessels: []
};

export default OwnerProfileSection;
